import {
  collection,
  doc,
  addDoc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  arrayUnion,
  runTransaction,
  deleteField,
} from "firebase/firestore";
import { db } from "../firebase";
import { findLocationMatch } from "./locationIdentity";
import { mergeParsedContact } from "./contactMerge";
import { shouldAutoClosePastJob } from "./billingReadiness";
import { invoiceLabel } from "./invoiceLabel";

// ── COMPANIES ──────────────────────────────────────────────
export async function createCompany(data) {
  return addDoc(collection(db, "companies"), {
    ...data,
    createdAt: serverTimestamp(),
  });
}

export async function getCompany(companyId) {
  const snap = await getDoc(doc(db, "companies", companyId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function updateCompany(companyId, data) {
  return updateDoc(doc(db, "companies", companyId), data);
}

// ── USERS / GUARDS ─────────────────────────────────────────
export async function getUser(userId) {
  const snap = await getDoc(doc(db, "users", userId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function updateUser(userId, data) {
  return updateDoc(doc(db, "users", userId), data);
}

export function watchGuards(companyId, callback) {
  const q = query(
    collection(db, "users"),
    where("companyId", "==", companyId),
    where("role", "==", "guard"),
    orderBy("name")
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
}

// ── DISPATCHERS ────────────────────────────────────────────
export async function getDispatchers(companyId) {
  const q = query(
    collection(db, "dispatchers"),
    where("companyId", "==", companyId)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function createDispatcher(data) {
  return addDoc(collection(db, "dispatchers"), {
    ...data,
    createdAt: serverTimestamp(),
  });
}

export async function updateDispatcher(id, data) {
  return updateDoc(doc(db, "dispatchers", id), data);
}

// ── BROKERS ────────────────────────────────────────────────
export async function getBrokers(companyId) {
  const q = query(
    collection(db, "brokers"),
    where("companyId", "==", companyId)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function createBroker(data) {
  return addDoc(collection(db, "brokers"), {
    ...data,
    createdAt: serverTimestamp(),
  });
}

export async function updateBroker(id, data) {
  return updateDoc(doc(db, "brokers", id), data);
}

// ── JOBS ───────────────────────────────────────────────────
export async function createJob(data) {
  const month = new Date().toISOString().slice(0, 7); // "2026-04"
  // Callers may pass `status` to override the default ("pool" or "confirmed")
  const fields = {
    invoiceStatus: "unpaid",
    billedAmount: 0,
    guardPayTotal: 0,
    net: 0,
    status: "intake",  // default — overridden if data.status is set
    ...data,
    month,
    createdAt: serverTimestamp(),
  };
  if (!isFerrariJob(data)) return addDoc(collection(db, "jobs"), fields);

  // Ferrari: the job gets an internal FX number from the company counter;
  // the order number the owner typed stays on the job's package.
  const ref = doc(collection(db, "jobs"));
  const companyRef = doc(db, "companies", data.companyId);
  await runTransaction(db, async (tx) => {
    const company = await tx.get(companyRef);
    const seq = (Number(company.data()?.jobCounters?.ferrari) || 0) + 1;
    tx.update(companyRef, { "jobCounters.ferrari": seq });
    tx.set(ref, { ...withOrderOnPackage(fields), jobNumber: ferrariJobNumber(seq) });
  });
  return ref;
}

// Same format as functions/intake/ferrari/jobNumber.js.
export const ferrariJobNumber = (n) => `FX-${String(n).padStart(4, "0")}`;
export const isFerrariJob = (job) => /ferrari/i.test(`${job?.brokerName || ""} ${job?.dispatcherName || ""}`);

// What to call a job in lists. Ferrari jobs go by their first order number,
// plus a count when combined ("MIW600032602 +3"); the FX number is
// internal. Everything else by its job number as before.
export function jobDisplayNumber(job) {
  if (isFerrariJob(job)) {
    const orders = [...new Set((job.packages || []).map((p) => String(p.hawbNumber || "").trim()).filter(Boolean))];
    if (orders.length) return orders.length > 1 ? `${orders[0]} +${orders.length - 1}` : orders[0];
  }
  return job?.jobNumber || job?.packages?.[0]?.hawbNumber || "";
}

// Keep a typed order number when the job number is replaced: put it on the
// first package with no number, or on a new package if there is none.
function withOrderOnPackage(fields) {
  const orderNo = String(fields.jobNumber || "").trim();
  const packages = fields.packages || [];
  if (!orderNo || /^FX-\d+$/i.test(orderNo) || packages.some((p) => String(p.hawbNumber || "").trim().toUpperCase() === orderNo.toUpperCase())) return fields;
  const blank = packages.findIndex((p) => !String(p.hawbNumber || "").trim());
  const next = blank >= 0
    ? packages.map((p, i) => (i === blank ? { ...p, hawbNumber: orderNo } : p))
    : [...packages, {
        id: Math.random().toString(36).slice(2, 9), hawbNumber: orderNo, description: "", declaredValue: "", weight: "", pieces: 1,
        stopId: (fields.stops || []).find((s) => s.type !== "airport")?.id || null,
      }];
  return { ...fields, packages: next };
}

export async function getJob(jobId) {
  const snap = await getDoc(doc(db, "jobs", jobId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function updateJob(jobId, data) {
  return updateDoc(doc(db, "jobs", jobId), data);
}

/**
 * Find an existing job by its broker job number (e.g. "MAKY26-2774") so a
 * follow-up dispatcher email can MERGE into it rather than spawning a
 * duplicate. Exact match on the trimmed jobNumber within the company.
 *
 * When more than one job shares the number (a duplicate already slipped
 * through, or a split job reuses the parent number), return the most recently
 * created LIVE one — that's the record a follow-up is almost certainly about.
 *
 * CANCELLED JOBS ARE NEVER A MATCH. Cancelling frees the number: the next
 * dispatch carrying it is a re-book, not a follow-up, and must become its own
 * job. Returns the job object ({ id, ...data }) or null when nothing live
 * matches.
 */
// Strip a trailing guard-split suffix (A/B, with or without a hyphen) to get
// the base job number. A dispatched 2922A/2922B and a bulk-added 2922 share a
// base, so they should recognize each other.
export function baseJobNumber(n) {
  return String(n || "").trim().replace(/-?[AB]$/i, "");
}

export async function findJobByNumber(companyId, jobNumber) {
  if (!companyId) return null;
  const trimmed = String(jobNumber || "").trim();
  if (!trimmed) return null;
  // Try the exact number first, then the base + its A/B variants, so a
  // dispatched 2922A finds an existing bulk-added 2922 (and vice-versa).
  const base = baseJobNumber(trimmed);
  // A number that ALREADY names a half only ever matches itself or the
  // un-split original. Offering the sibling here meant a follow-up for
  // 4541A could land on 4541B — the other half of the same dispatch, a
  // different city with different people — and merge into it. A bare base
  // still reaches for its halves, which is the case this list was for.
  const suffixed = /-?[AB]$/i.test(trimmed);
  const candidates = Array.from(new Set(
    (suffixed
      ? [trimmed, base]
      : [trimmed, `${base}A`, `${base}B`, `${base}-A`, `${base}-B`]
    ).filter(Boolean)
  ));
  for (const cand of candidates) {
    // eslint-disable-next-line no-await-in-loop
    const snap = await getDocs(query(
      collection(db, "jobs"),
      where("companyId", "==", companyId),
      where("jobNumber", "==", cand),
    ));
    if (snap.empty) continue;
    const matches = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const live = matches.filter((j) => j.status !== "cancelled");
    // Every match cancelled? Then this number is FREE — a cancelled job is not
    // something a new dispatch should merge into. Returning the dead job made
    // a corrected re-upload land on it: the bulk save folds in stops and never
    // touches status or scheduledDate, so all the caller got was a no-op merge
    // onto a cancelled doc still carrying the wrong dates, and nothing showed
    // up in the inbox. Skip to the next candidate; if none is live we return
    // null and the caller creates a fresh job, which is the right outcome.
    if (!live.length) continue;
    live.sort((a, b) => {
      const at = a.createdAt?.toMillis?.() || 0;
      const bt = b.createdAt?.toMillis?.() || 0;
      return bt - at;
    });
    return live[0];
  }
  return null;
}

/**
 * Find an existing job by a Ferrari tracking number (e.g. NYW600239567).
 * Used by the re-upload flow: a SIGNED copy of an already-created Ferrari
 * job should ADVANCE that job's status, not create a duplicate.
 *
 * Ferrari jobs store the tracking number as their jobNumber, so try the
 * cheap exact jobNumber query first. Older jobs may carry it only on
 * packages[].hawbNumber (jobNumber blank) — Firestore can't query into
 * an array of maps, so fall back to a company-wide scan (same
 * full-collection pattern as closeBilledActiveJobs; fine at this scale).
 * Prefers a non-cancelled, most-recently-created match, like
 * findJobByNumber.
 */
export async function findJobByTrackingNumber(companyId, trackingNumber) {
  if (!companyId) return null;
  const wanted = String(trackingNumber || "").trim().toLowerCase();
  if (!wanted) return null;
  const pick = (matches) => {
    if (!matches.length) return null;
    const live = matches.filter((j) => j.status !== "cancelled");
    const pool = live.length ? live : matches;
    pool.sort((a, b) => {
      const at = a.createdAt?.toMillis?.() || 0;
      const bt = b.createdAt?.toMillis?.() || 0;
      return bt - at;
    });
    return pool[0];
  };
  try {
    const byNumber = await getDocs(query(
      collection(db, "jobs"),
      where("companyId", "==", companyId),
      where("jobNumber", "==", String(trackingNumber).trim()),
    ));
    const hit = pick(byNumber.docs.map((d) => ({ id: d.id, ...d.data() })));
    if (hit) return hit;
  } catch (err) {
    console.warn("findJobByTrackingNumber: jobNumber query failed", err);
  }
  try {
    const snap = await getDocs(query(
      collection(db, "jobs"),
      where("companyId", "==", companyId),
    ));
    const matches = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((j) =>
        String(j.jobNumber || "").trim().toLowerCase() === wanted
        || (j.packages || []).some((p) => String(p.hawbNumber || "").trim().toLowerCase() === wanted));
    return pick(matches);
  } catch (err) {
    console.warn("findJobByTrackingNumber: scan failed", err);
    return null;
  }
}

/**
 * Merge follow-up stops/packages into an existing job. Used when a broker
 * sends an update email adding packages or stops to a job that's already
 * been uploaded (e.g. a second Malca email for MAKY26-2774).
 *
 * Dedup rules:
 *   - Stops: matched by normalized (locationName, address). An incoming
 *     airport stop folds into the job's existing airport stop rather than
 *     adding a second one. Matched stops are skipped, not duplicated.
 *   - Packages: matched by normalized HAWB number. A package whose HAWB is
 *     already on the job is skipped. Blank-HAWB packages can't be deduped,
 *     so they're always appended.
 *
 * Because an incoming package's stopId points at an incoming stop, we remap
 * it: if that stop deduped to an existing one, the package repoints at the
 * existing stop's id; otherwise it keeps the newly-appended stop's id.
 *
 * Bumps updatedAt and writes an audit entry. Returns the per-type counts
 * { addedStops, skippedStops, addedPackages, skippedPackages } so the caller
 * can show a toast.
 */
export async function mergeJob(jobId, { stops = [], packages = [], scheduledDate = null, scheduledTime = null } = {}, actor = {}) {
  if (!jobId) throw new Error("jobId required");
  const ref = doc(db, "jobs", jobId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Job not found");
  const job = snap.data();

  const mergedStops = Array.isArray(job.stops) ? [...job.stops] : [];
  const mergedPackages = Array.isArray(job.packages) ? [...job.packages] : [];
  const newId = () => Math.random().toString(36).slice(2, 9);
  const stopKey = (s) => `${normKey(s?.locationName)}|${normKey(s?.address)}`;

  // Index existing stops by match-key so we can both detect dupes and find
  // the id to repoint incoming packages onto.
  const existingStopIdByKey = new Map();
  for (const s of mergedStops) existingStopIdByKey.set(stopKey(s), s.id);

  // incoming stop id → resolved stop id in the merged array.
  const stopIdRemap = new Map();
  let addedStops = 0, skippedStops = 0;
  for (const s of stops) {
    // Fold any incoming airport stop into the job's existing one.
    if (s.type === "airport") {
      const air = mergedStops.find((e) => e.type === "airport");
      if (air) { if (s.id) stopIdRemap.set(s.id, air.id); skippedStops++; continue; }
    }
    const key = stopKey(s);
    if (existingStopIdByKey.has(key)) {
      if (s.id) stopIdRemap.set(s.id, existingStopIdByKey.get(key));
      skippedStops++;
      continue;
    }
    const id = s.id || newId();
    // Keep the outbound invariant: the airport is the LAST stop. If the
    // job already ends in an airport, splice new pickups in before it
    // instead of appending after — otherwise the airport lands in the
    // middle and the guard view treats the last pickup as the airport.
    // Inbound (airport first) and direct (no airport) just append.
    const lastIsAirport = mergedStops.length > 0 && mergedStops[mergedStops.length - 1].type === "airport";
    if (lastIsAirport) {
      mergedStops.splice(mergedStops.length - 1, 0, { ...s, id });
    } else {
      mergedStops.push({ ...s, id });
    }
    existingStopIdByKey.set(key, id);
    if (s.id) stopIdRemap.set(s.id, id);
    addedStops++;
  }

  // Dedup packages by normalized HAWB (non-blank only).
  const existingHawbs = new Set(
    mergedPackages.map((p) => normKey(p.hawbNumber)).filter(Boolean)
  );
  const mergeStamp = new Date().toISOString();
  let addedPackages = 0, skippedPackages = 0;
  for (const p of packages) {
    const hawb = normKey(p.hawbNumber);
    if (hawb && existingHawbs.has(hawb)) { skippedPackages++; continue; }
    const remappedStopId = (p.stopId != null && stopIdRemap.has(p.stopId))
      ? stopIdRemap.get(p.stopId)
      : (p.stopId || null);
    mergedPackages.push({
      ...p,
      id: p.id || newId(),
      stopId: remappedStopId,
      // Stamp provenance so reporting can tell merged-in packages from
      // ones present at job creation. Don't clobber an existing source.
      source: p.source || {
        type: "merge_followup",
        addedByUserId: actor.id || null,
        addedByName: actor.name || "",
        addedAt: mergeStamp,
      },
    });
    if (hawb) existingHawbs.add(hawb);
    addedPackages++;
  }

  // A follow-up carrying a corrected date should MOVE the job. Only stops
  // and packages were written before, so a re-send whose whole point was
  // fixing the date landed as a no-op and the job kept the wrong day.
  // Caller opts in by passing the values; nothing changes when it doesn't.
  const datePatch = {};
  if (scheduledDate && scheduledDate !== job.scheduledDate) datePatch.scheduledDate = scheduledDate;
  if (scheduledTime && scheduledTime !== job.scheduledTime) datePatch.scheduledTime = scheduledTime;
  const movedFrom = datePatch.scheduledDate ? (job.scheduledDate || "(none)") : null;

  await updateDoc(ref, {
    stops: mergedStops,
    packages: mergedPackages,
    ...datePatch,
    updatedAt: serverTimestamp(),
  });

  // A merge that changed NOTHING is the dangerous one: it reads as success
  // to the caller and leaves the job exactly as it was. Say so plainly
  // rather than logging "+0 pkg · +0 stops", which looks like work done.
  const noop = addedStops === 0 && addedPackages === 0 && Object.keys(datePatch).length === 0;

  // Best-effort audit — never block the merge on it.
  try {
    await logAudit({
      jobId,
      jobNumber: job.jobNumber || "",
      companyId: job.companyId || null,
      action: noop
        ? "Follow-up merged — NOTHING ADDED (every stop and package was already on the job)"
        : `Follow-up merged · +${addedPackages} pkg · +${addedStops} stop${addedStops !== 1 ? "s" : ""}`
          + (movedFrom ? ` · date ${movedFrom} → ${datePatch.scheduledDate}` : "")
          + (datePatch.scheduledTime ? ` · time → ${datePatch.scheduledTime}` : ""),
      userId: actor.id || null,
      userName: actor.name || "",
      details: { addedStops, skippedStops, addedPackages, skippedPackages, ...datePatch, movedFrom, noop },
    });
  } catch (err) {
    console.warn("Audit log failed for job merge", err);
  }

  return { addedStops, skippedStops, addedPackages, skippedPackages, noop, movedFrom, ...datePatch };
}

/**
 * One-time cleanup: close out jobs that have already been billed
 * (invoiceStatus invoiced/sent/paid) but are still sitting in an ACTIVE
 * workflow status (intake/pool/grabbed/confirmed/inProgress). This is
 * almost entirely DAI flat-fee directs — they get forwarded/invoiced
 * without a guard ever working them, so their workflow status never
 * advanced and they lingered in the Job Inbox as "active" forever.
 * Setting status="complete" drops them out of the active inbox while
 * leaving all billing untouched. Idempotent. Returns the count closed.
 */
export async function closeBilledActiveJobs(companyId) {
  if (!companyId) return 0;
  const ACTIVE = new Set(["intake", "pool", "grabbed", "confirmed", "inProgress"]);
  const BILLED = new Set(["invoiced", "sent", "paid"]);
  let snap;
  try {
    snap = await getDocs(query(
      collection(db, "jobs"),
      where("companyId", "==", companyId),
    ));
  } catch (err) {
    console.warn("closeBilledActiveJobs: couldn't read jobs", err);
    return 0;
  }
  let closed = 0;
  for (const d of snap.docs) {
    const j = d.data();
    if (!BILLED.has(j.invoiceStatus)) continue;
    if (!ACTIVE.has(j.status)) continue;
    try {
      // eslint-disable-next-line no-await-in-loop
      await updateDoc(d.ref, {
        status: "complete",
        completedAt: j.completedAt || new Date(),
      });
      closed++;
    } catch (err) {
      console.warn(`closeBilledActiveJobs: couldn't close job ${d.id}`, err?.code || err?.message);
    }
  }
  return closed;
}

/**
 * Close out DAI (bundled flat-rate) directs whose date has STRICTLY
 * passed. DAI jobs bill as forwarded batches, not per job, and are
 * rarely worked through the guard app — so without this they sat in
 * the active Job inbox as "In pool" until the whole batch was
 * forwarded, even though the trip had already run. Marking them
 * complete the day AFTER their date (never on the day — the run may
 * still be underway) drops them off the active inbox while leaving
 * batch readiness / forwarding untouched. Matches the "✅ past" badge
 * on the Bundled tab's expanded rows exactly. Jobs a guard is actively
 * working (inProgress) are left alone. Idempotent; returns the count.
 *
 * The rule itself (shouldAutoClosePastJob) lives in billingReadiness.js
 * beside the other readiness rules, unit-tested; `todayYmd` is injected
 * so it matches the caller's local-date derivation.
 */
export async function closePastDaiJobs(companyId, todayYmd) {
  if (!companyId || !todayYmd) return 0;
  let snap;
  try {
    snap = await getDocs(query(
      collection(db, "jobs"),
      where("companyId", "==", companyId),
    ));
  } catch (err) {
    console.warn("closePastDaiJobs: couldn't read jobs", err);
    return 0;
  }
  let closed = 0;
  for (const d of snap.docs) {
    const j = d.data();
    if (!shouldAutoClosePastJob(j, todayYmd)) continue;
    try {
      // eslint-disable-next-line no-await-in-loop
      await updateDoc(d.ref, {
        status: "complete",
        completedAt: j.completedAt || new Date(),
        autoClosedReason: "date_passed",
      });
      closed++;
    } catch (err) {
      console.warn(`closePastDaiJobs: couldn't close job ${d.id}`, err?.code || err?.message);
    }
  }
  return closed;
}

/**
 * Self-heal: copy broker rate-card flags onto ACTIVE jobs that are
 * missing them. AI-parsed jobs historically never inherited
 * requiresRepConfirm or the OOT follow rule (only the manual broker
 * dropdown copied them), so day-before rep-confirm reminders skipped
 * every parsed job. Only upgrades: sets requiresRepConfirm true when
 * the broker card says so, and fills empty ootFollow fields — never
 * clears anything the job already carries. Completed/cancelled jobs
 * are left alone (reminders don't apply to them). Idempotent; returns
 * the count patched.
 */
export async function syncBrokerFlagsToActiveJobs(companyId) {
  if (!companyId) return 0;
  const ACTIVE = new Set(["intake", "pool", "grabbed", "confirmed", "inProgress"]);
  let jobSnap, brokerSnap;
  try {
    [jobSnap, brokerSnap] = await Promise.all([
      getDocs(query(collection(db, "jobs"), where("companyId", "==", companyId))),
      getDocs(query(collection(db, "brokers"), where("companyId", "==", companyId))),
    ]);
  } catch (err) {
    console.warn("syncBrokerFlagsToActiveJobs: couldn't read jobs/brokers", err);
    return 0;
  }
  const brokers = brokerSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  // Same loose matching as the inbox's rateCardDispatcher: id first, then
  // exact name, then substring either way.
  const brokerFor = (j) => {
    if (j.brokerId) {
      const byId = brokers.find((b) => b.id === j.brokerId);
      if (byId) return byId;
    }
    const wanted = (j.brokerName || "").toLowerCase();
    if (!wanted) return null;
    return brokers.find((b) => (b.name || "").toLowerCase() === wanted)
      || brokers.find((b) => {
        const bn = (b.name || "").toLowerCase();
        return bn && (wanted.includes(bn) || bn.includes(wanted));
      })
      || null;
  };
  let patched = 0;
  for (const d of jobSnap.docs) {
    const j = d.data();
    if (!ACTIVE.has(j.status)) continue;
    const broker = brokerFor(j);
    if (!broker) continue;
    const patch = {};
    if (broker.requiresRepConfirm === true && j.requiresRepConfirm !== true) {
      patch.requiresRepConfirm = true;
    }
    if (broker.ootFollowMinutes && !j.ootFollowMinutes) patch.ootFollowMinutes = broker.ootFollowMinutes;
    if (broker.ootFollowMiles && !j.ootFollowMiles) patch.ootFollowMiles = broker.ootFollowMiles;
    if (Object.keys(patch).length === 0) continue;
    try {
      // eslint-disable-next-line no-await-in-loop
      await updateDoc(d.ref, patch);
      patched++;
    } catch (err) {
      console.warn(`syncBrokerFlagsToActiveJobs: couldn't patch job ${d.id}`, err?.code || err?.message);
    }
  }
  return patched;
}

/**
 * Apply a partial update to every job sharing a bundleId (e.g. all DAI
 * direct jobs created from one schedule email). Skips jobs that have
 * already been invoiced — once a bill is out the door, edits to the
 * underlying job would create reconciliation headaches.
 *
 * Returns { updated, skipped } counts so the caller can show a toast.
 */
export async function updateBundle(companyId, bundleId, partial) {
  if (!bundleId) throw new Error("bundleId required");
  const q = query(
    collection(db, "jobs"),
    where("companyId", "==", companyId),
    where("bundleId", "==", bundleId),
  );
  const snap = await getDocs(q);
  let updated = 0;
  let skipped = 0;
  // Truly-locked invoice states. Note that the createJob default is
  // "unpaid" (a billing-readiness flag, not a "this has been billed"
  // marker), so we don't lock that — it's just the resting state for any
  // job that hasn't yet been processed through ReadyToBill.
  const LOCKED = new Set(["pending", "invoiced", "sent", "paid"]);
  // Sequential, not Promise.all — keeps Firestore happy on large bundles
  // and makes the audit log easier to follow if something fails halfway.
  for (const d of snap.docs) {
    const j = d.data();
    if (LOCKED.has(j.invoiceStatus)) {
      skipped++;
      continue;
    }
    // eslint-disable-next-line no-await-in-loop
    await updateDoc(d.ref, partial);
    updated++;
  }
  return { updated, skipped };
}

/**
 * List the jobs in a bundle. Used by the detail modal to show "this is
 * job 3 of 6 in this DAI bundle" context and the bulk-edit count.
 */
export async function getBundleJobs(companyId, bundleId) {
  if (!bundleId) return [];
  const q = query(
    collection(db, "jobs"),
    where("companyId", "==", companyId),
    where("bundleId", "==", bundleId),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Status/assignment invariant checker. Returns the patch needed to make
 * job.status consistent with job.guardAssignments — or null if the record
 * is already consistent.
 *
 * Rules:
 *   - guard-bound statuses (grabbed/confirmed/inProgress) require ≥1 guard.
 *     If the array is empty, fall back to "intake" so the owner sees it
 *     in the unassigned bucket.
 *   - "pool" with a populated guardAssignments array is also a violation
 *     (a pool job has no slot owner). Detach the assignments so the slot
 *     is genuinely open for the next grabber.
 *
 * Terminal statuses (complete/cancelled/invoiced) are left alone — they
 * legitimately retain their final assignment list as the historical record.
 */
export function deriveJobStatusFix(job) {
  if (!job) return null;
  const assigned = (job.guardAssignments || []).filter((g) => g?.guardId);
  const guardBound = new Set(["grabbed", "confirmed", "inProgress"]);
  if (guardBound.has(job.status) && assigned.length === 0) {
    return { status: "intake" };
  }
  if (job.status === "pool" && assigned.length > 0) {
    const guardIds = [...new Set(assigned.map((g) => g.guardId).filter(Boolean))];
    // Strip the stale slot — but if the pool flag is set because a guard
    // walked away, keep the rest of the array (multi-guard jobs may have
    // other slots still filled).
    return {
      guardAssignments: [],
      guardIds: [],
      // Reset the breadcrumb fields too so nothing downstream thinks
      // there's still a guard on it.
      grabbedByGuardId: null,
      grabbedGuardNumber: null,
      // Leave the unused linter-pleaser; guardIds derived above.
      _heal_was_assigned_in_pool: guardIds,
    };
  }
  return null;
}

/**
 * Apply deriveJobStatusFix and write back. No-op if already consistent.
 * Stripped of the diagnostic _heal_ field before the write.
 */
export async function healJobStatusInvariant(job) {
  const fix = deriveJobStatusFix(job);
  if (!fix) return false;
  const { _heal_was_assigned_in_pool, ...patch } = fix;
  await updateDoc(doc(db, "jobs", job.id), patch);
  return true;
}

// Append a package to a job with full provenance/source tracking.
// source.type identifies where the package came from so we can later
// report on "how often do guards scan unexpected packages at the airport?"
// Supported source.type values:
//   - "owner_manifest"       — added by owner during job creation (default for legacy packages)
//   - "guard_scan_airport"   — guard scanned a POD form at the airport
//   - "dispatcher_add"       — added by dispatcher (future)
//   - "broker_add"           — added by broker (future)
export async function addPackageToJob(jobId, packageData, user, source) {
  const jobRef = doc(db, "jobs", jobId);
  const snap = await getDoc(jobRef);
  const existing = snap.data()?.packages || [];

  const newPackage = {
    id: Math.random().toString(36).slice(2, 9),
    hawbNumber:    packageData.hawbNumber    || "",
    description:   packageData.description   || "",
    declaredValue: packageData.declaredValue ?? "",
    pieces:        packageData.pieces        ?? null,
    weight:        packageData.weight        || "",
    shipper:       packageData.shipper       || "",
    consignee:     packageData.consignee     || "",
    photos: {},
    source: {
      type:            source?.type            || "guard_scan_airport",
      addedByUserId:   user?.id                || null,
      addedByName:     user?.name              || "",
      addedByRole:     user?.role              || "guard",
      addedAt:         new Date().toISOString(),
      scanPhotoUrl:    source?.scanPhotoUrl    || null,
      extractionModel: source?.extractionModel || null,
    },
  };

  await updateDoc(jobRef, { packages: [...existing, newPackage] });

  await logAudit({
    jobId,
    userId:   user?.id || null,
    userName: user?.name || "",
    userRole: user?.role || "guard",
    action:   "package_added",
    details: {
      source:        newPackage.source.type,
      hawbNumber:    newPackage.hawbNumber,
      description:   newPackage.description,
      declaredValue: newPackage.declaredValue,
    },
  });

  return newPackage;
}

// Mark an expected package as "didn't arrive on the flight" — used when the
// guard reaches the airport and the manifest package isn't actually on the
// plane. Enables the "Leave airport" step to proceed without that package's
// photos and feeds into reporting on flight-miss frequency.
export async function markPackageNotOnFlight(jobId, packageIndex, user, reason) {
  const jobRef = doc(db, "jobs", jobId);
  const snap = await getDoc(jobRef);
  const packages = [...(snap.data()?.packages || [])];
  if (!packages[packageIndex]) throw new Error("Package not found");

  packages[packageIndex] = {
    ...packages[packageIndex],
    notOnFlight:       true,
    notOnFlightAt:     new Date().toISOString(),
    notOnFlightBy: {
      userId:   user?.id   || null,
      userName: user?.name || "",
      userRole: user?.role || "guard",
    },
    notOnFlightReason: reason || "",
  };
  await updateDoc(jobRef, { packages });
  await logAudit({
    jobId,
    userId:   user?.id   || null,
    userName: user?.name || "",
    userRole: user?.role || "guard",
    action:   "package_not_on_flight",
    details: {
      hawbNumber: packages[packageIndex].hawbNumber,
      reason:     reason || "",
    },
  });
}

export async function unmarkPackageNotOnFlight(jobId, packageIndex, user) {
  const jobRef = doc(db, "jobs", jobId);
  const snap = await getDoc(jobRef);
  const packages = [...(snap.data()?.packages || [])];
  if (!packages[packageIndex]) throw new Error("Package not found");

  const { notOnFlight, notOnFlightAt, notOnFlightBy, notOnFlightReason, ...rest } = packages[packageIndex];
  packages[packageIndex] = rest;
  await updateDoc(jobRef, { packages });
  await logAudit({
    jobId,
    userId:   user?.id   || null,
    userName: user?.name || "",
    userRole: user?.role || "guard",
    action:   "package_not_on_flight_reverted",
    details: { hawbNumber: rest.hawbNumber },
  });
}

// ── Vaulting / held pieces ───────────────────────────────────────────
// A "held" package is one kept (usually overnight at the owner's house)
// because it couldn't be delivered/flown that day. It stays on its origin
// job, flagged, until it's attached to a later carrier job (any inbound or
// outbound that actually carries it). Looked up by package id, not index,
// because held pieces move between jobs. Both guard and owner can flag.
export async function markPackageHeld(jobId, pkgId, user, reason = "") {
  const jobRef = doc(db, "jobs", jobId);
  const snap = await getDoc(jobRef);
  const data = snap.data() || {};
  const packages = [...(data.packages || [])];
  const idx = packages.findIndex((p) => p.id === pkgId);
  if (idx < 0) throw new Error("Package not found");
  packages[idx] = {
    ...packages[idx],
    held:   true,
    heldAt: new Date().toISOString(),
    heldBy: {
      userId:   user?.id   || null,
      userName: user?.name || "",
      userRole: user?.role || "owner",
    },
    heldReason:        reason || "",
    // Stamp the origin once so nights-held + provenance survive the move
    // onto a later carrier job. Don't overwrite if already set.
    heldFromJobId:     packages[idx].heldFromJobId || jobId,
    heldFromJobNumber: packages[idx].heldFromJobNumber || data.jobNumber || "",
  };
  await updateDoc(jobRef, { packages });
  await logAudit({
    jobId,
    userId:   user?.id   || null,
    userName: user?.name || "",
    userRole: user?.role || "owner",
    action:   "package_held",
    details:  { hawbNumber: packages[idx].hawbNumber, reason: reason || "" },
  });
}

export async function unmarkPackageHeld(jobId, pkgId, user) {
  const jobRef = doc(db, "jobs", jobId);
  const snap = await getDoc(jobRef);
  const packages = [...(snap.data()?.packages || [])];
  const idx = packages.findIndex((p) => p.id === pkgId);
  if (idx < 0) throw new Error("Package not found");
  const { held, heldAt, heldBy, heldReason, heldFromJobId, heldFromJobNumber, ...rest } = packages[idx];
  packages[idx] = rest;
  await updateDoc(jobRef, { packages });
  await logAudit({
    jobId,
    userId:   user?.id   || null,
    userName: user?.name || "",
    userRole: user?.role || "owner",
    action:   "package_held_reverted",
    details:  { hawbNumber: rest.hawbNumber },
  });
}

export function watchJobs(companyId, statusFilter, callback) {
  let q;
  if (statusFilter && statusFilter.length > 0) {
    q = query(
      collection(db, "jobs"),
      where("companyId", "==", companyId),
      where("status", "in", statusFilter),
      orderBy("createdAt", "desc")
    );
  } else {
    q = query(
      collection(db, "jobs"),
      where("companyId", "==", companyId),
      orderBy("createdAt", "desc")
    );
  }
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
}

export function watchGuardJobs(guardId, callback, onError) {
  // Note: Firestore does not allow combining array-contains with 'in' in one query.
  // Fetch all jobs for this guard and filter by status client-side.
  const q = query(
    collection(db, "jobs"),
    where("guardIds", "array-contains", guardId),
    orderBy("createdAt", "desc")
  );
  const relevant = new Set(["grabbed", "confirmed", "inProgress", "complete", "cancelled"]);
  return onSnapshot(
    q,
    (snap) => {
      callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((j) => relevant.has(j.status)));
    },
    (err) => {
      console.error("watchGuardJobs listener error", err);
      if (onError) onError(err);
    }
  );
}

export function watchJobPool(companyId, callback) {
  const q = query(
    collection(db, "jobs"),
    where("companyId", "==", companyId),
    where("status", "==", "pool"),
    orderBy("createdAt", "desc")
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
}

// ── JOB STATUS TRANSITIONS ─────────────────────────────────
export async function releaseJobToPool(jobId) {
  return updateDoc(doc(db, "jobs", jobId), { status: "pool" });
}

export async function guardGrabJob(jobId, guardId, guardNumber, guardName, existingAssignments, existingGuardIds) {
  const newAssignment = { guardNumber, guardId, guardName: guardName || "", payRate: "" };
  const updatedAssignments = [
    ...(existingAssignments || []).filter((a) => a.guardNumber !== guardNumber),
    newAssignment,
  ];
  const updatedGuardIds = [...new Set([...(existingGuardIds || []), guardId])];
  return updateDoc(doc(db, "jobs", jobId), {
    status: "grabbed",
    grabbedByGuardId: guardId,
    grabbedGuardNumber: guardNumber,
    grabbedAt: serverTimestamp(),
    guardAssignments: updatedAssignments,
    guardIds: updatedGuardIds,
  });
}

export async function ownerConfirmGuard(jobId) {
  return updateDoc(doc(db, "jobs", jobId), {
    status: "confirmed",
    confirmedAt: serverTimestamp(),
  });
}

/**
 * Owner reassigns the guard at a given guardNumber slot.
 *   newGuard = { id, name, defaultPayRate? } to assign, or null to unassign.
 * Keeps the denormalized guardIds array in sync and writes an audit entry.
 */
export async function reassignGuard(jobId, guardNumber, newGuard, actor = {}) {
  const jobRef = doc(db, "jobs", jobId);
  const snap = await getDoc(jobRef);
  if (!snap.exists()) throw new Error("Job not found");
  const job = snap.data();

  const existing = (job.guardAssignments || []).find((g) => g.guardNumber === guardNumber);
  const rest = (job.guardAssignments || []).filter((g) => g.guardNumber !== guardNumber);

  let updated;
  if (newGuard?.id) {
    updated = [
      ...rest,
      {
        guardNumber,
        guardId: newGuard.id,
        guardName: newGuard.name || "",
        // Preserve any pay-rate override the owner had set; otherwise use the
        // new guard's default; otherwise blank.
        payRate: existing?.payRate || newGuard.defaultPayRate || "",
        reassignedAt: new Date().toISOString(),
        previousGuardId: existing?.guardId || null,
        previousGuardName: existing?.guardName || "",
      },
    ];
  } else {
    updated = rest;
  }

  // Rebuild guardIds from the full assignment list so old guards drop off.
  const guardIds = [...new Set(updated.map((g) => g.guardId).filter(Boolean))];

  // Keep status in sync with reality. If the owner just emptied the last
  // slot, the job can no longer be "grabbed/confirmed/inProgress" — those
  // states all imply a guard is on it. Fall back to "intake" so the owner
  // sees it in the unassigned bucket and re-pools or reassigns deliberately.
  // Don't touch terminal states (complete/cancelled/invoiced/etc).
  const guardBoundStatuses = new Set(["grabbed", "confirmed", "inProgress"]);
  const patch = { guardAssignments: updated, guardIds };
  if (updated.length === 0 && guardBoundStatuses.has(job.status)) {
    patch.status = "intake";
  }

  await updateDoc(jobRef, patch);

  // Audit log — best-effort; never block the reassignment on this.
  try {
    await logAudit({
      action: "guard_reassigned",
      jobId,
      jobNumber: job.jobNumber || "",
      companyId: job.companyId || null,
      guardNumber,
      previousGuardId: existing?.guardId || null,
      previousGuardName: existing?.guardName || "",
      newGuardId: newGuard?.id || null,
      newGuardName: newGuard?.name || "",
      actorId: actor.id || null,
      actorName: actor.name || "",
    });
  } catch (err) {
    console.warn("Audit log failed for guard reassignment", err);
  }
}

// Guard backs out of a job they previously grabbed. We need to fully detach
// them — not just flip status — otherwise their entry stays in
// guardAssignments and the slot looks "filled" to the next guard browsing
// the pool. We accept guardId so we can remove the right slot even if the
// caller doesn't know which guardNumber they were in.
export async function guardReleaseJob(jobId, guardId = null) {
  const ref = doc(db, "jobs", jobId);
  const snap = await getDoc(ref);
  const job = snap.exists() ? snap.data() : {};
  const releasingId = guardId || job.grabbedByGuardId || null;

  // Capture the assignment record for the releasing guard before we drop
  // it — we want their name and the original grab time on the history
  // entry so the owner can audit later without a join.
  const releasingAssignment = (job.guardAssignments || [])
    .find((g) => g.guardId === releasingId) || null;

  // Drop the releasing guard from the assignment array, then rebuild
  // guardIds from what's left so denormalized queries stay in sync.
  const remainingAssignments = (job.guardAssignments || [])
    .filter((g) => g.guardId !== releasingId);
  const remainingGuardIds = [
    ...new Set(remainingAssignments.map((g) => g.guardId).filter(Boolean)),
  ];

  // Append a release-history entry on the job itself. Inline because the
  // job-detail screen wants to show "released 2× before" without a second
  // collection lookup. ISO string (not serverTimestamp) because Firestore
  // doesn't allow sentinel timestamps inside arrayUnion payloads.
  const historyEntry = {
    guardId: releasingId,
    guardName: releasingAssignment?.guardName || "",
    guardNumber: releasingAssignment?.guardNumber || job.grabbedGuardNumber || null,
    releasedAt: new Date().toISOString(),
    // What state was the job in before the release? Useful for telling
    // "released after confirmation" (worse) from "released same day they
    // grabbed it" (less bad).
    previousStatus: job.status || null,
    // Time elapsed between grab and release, in minutes — null if we
    // can't compute it.
    heldMinutes: (() => {
      const grabbed = job.grabbedAt?.toDate?.() || (job.grabbedAt ? new Date(job.grabbedAt) : null);
      if (!grabbed) return null;
      return Math.round((Date.now() - grabbed.getTime()) / 60000);
    })(),
  };

  // Best-effort audit log entry — supports cross-job aggregation later
  // ("which guards release most often?"). Failures don't block the
  // release itself; the on-job history is the source of truth for UI.
  try {
    await logAudit({
      action: "guard_released_job",
      jobId,
      jobNumber: job.jobNumber || "",
      companyId: job.companyId || null,
      guardId: releasingId,
      guardName: releasingAssignment?.guardName || "",
      previousStatus: job.status || null,
      heldMinutes: historyEntry.heldMinutes,
    });
  } catch (err) {
    console.warn("Audit log failed for guard release", err);
  }

  return updateDoc(ref, {
    status: "pool",
    grabbedByGuardId: null,
    grabbedGuardNumber: null,
    releasedAt: serverTimestamp(),
    guardReleased: true,
    guardAssignments: remainingAssignments,
    guardIds: remainingGuardIds,
    releaseHistory: arrayUnion(historyEntry),
  });
}

export async function startJob(jobId) {
  return updateDoc(doc(db, "jobs", jobId), {
    status: "inProgress",
    startedAt: serverTimestamp(),
  });
}

// Recursively drop any undefined values from an object so updateDoc() can
// accept it. Firestore rejects undefined; nulls are fine.
function stripUndefined(value) {
  if (value === undefined) return null;
  if (value === null || typeof value !== "object") return value;
  if (Array.isArray(value)) {
    return value
      .map((v) => stripUndefined(v))
      .filter((v) => v !== null || true); // keep nulls, drop nothing
  }
  const out = {};
  for (const [k, v] of Object.entries(value)) {
    if (v === undefined) continue;          // drop undefined keys outright
    out[k] = stripUndefined(v);
  }
  return out;
}

export async function completeJob(jobId, billingData) {
  const month = new Date().toISOString().slice(0, 7);
  const completedAtMs = Date.now();

  // Auto-compute billedAmount from the broker's rate card if we can find a match.
  // This is a best-effort pass — if anything fails, the job still completes and
  // the owner can fill billing in manually via the Review modal.
  let autoBilling = {};
  try {
    const jobSnap = await getDoc(doc(db, "jobs", jobId));
    if (jobSnap.exists()) {
      const job = jobSnap.data();
      if (job.companyId && job.brokerName) {
        const { computeJobBilling } = await import("./billing");
        const bq = query(
          collection(db, "brokers"),
          where("companyId", "==", job.companyId)
        );
        const bsnap = await getDocs(bq);
        const wanted = (job.brokerName || "").toLowerCase();
        const broker = bsnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .find((b) => (b.name || "").toLowerCase() === wanted)
          || bsnap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .find((b) => wanted.includes((b.name || "").toLowerCase()) || (b.name || "").toLowerCase().includes(wanted));
        if (broker) {
          const billing = computeJobBilling(
            { ...job, completedAt: completedAtMs },
            broker
          );
          autoBilling = {
            brokerId: broker.id,
            billedAmount: billing.total,
            billingBreakdown: {
              ...billing,
              lineItems: billing.lineItems,
              computedAt: completedAtMs,
              rateCardVersion: broker.rateCardVersion || null,
            },
            invoiceStatus: "pending",
          };
        }
      }
    }
  } catch (err) {
    // Swallow — don't block job completion on billing
    console.warn("Auto-billing compute failed:", err);
  }

  return updateDoc(doc(db, "jobs", jobId), stripUndefined({
    status: "complete",
    completedAt: serverTimestamp(),
    completedAtMs,
    month,
    ...autoBilling,
    ...billingData,
  }));
}

export async function cancelJob(jobId, reason) {
  return updateDoc(doc(db, "jobs", jobId), {
    status: "cancelled",
    cancelReason: reason,
    cancelledAt: serverTimestamp(),
  });
}

// The status a cancelled job goes back to: confirmed when it still has
// guards, tentative ("intake") when it came from email and was never
// released, otherwise the pool. Mirrors functions/intake/resolve.js.
export function restoredStatus(job) {
  if ((job.guardIds || []).length) return "confirmed";
  if (job.intake?.source === "email" && job.intake?.state === "tentative") return "intake";
  return "pool";
}

// Undo a cancel. A job that was combined into another stays cancelled —
// its orders already live on the combined job.
export async function restoreJob(job) {
  if (job.combinedInto) throw new Error("This job was combined into another; restore that one instead.");
  return updateDoc(doc(db, "jobs", job.id), {
    status: restoredStatus(job),
    cancelReason: deleteField(),
    cancelledAt: deleteField(),
    restoredAt: serverTimestamp(),
  });
}

/**
 * Hard-delete a job and any draft invoices created from it. Use sparingly —
 * meant for test-data cleanup, not normal job lifecycle. Cancel a real job via
 * cancelJob() instead, which preserves the audit trail.
 *
 * Cleans up:
 *   - the job doc
 *   - any draft invoices that reference this jobId
 *   - audit log entries are LEFT alone (cheap insurance: someone can still
 *     answer "did this job ever exist?")
 */
/**
 * Create a sandbox test job suitable for walking a new guard through the
 * full lifecycle. Pre-fills harmless data so the owner doesn't have to type
 * a real schedule for testing. Pair with the Guard Test Kit doc.
 *
 * - jobNumber: "TEST-{timestamp}" so it never collides with real Malca numbers
 * - jobType: outbound (most-used direction)
 * - 2 stops + airport
 * - All addresses are real Charlotte locations so tap-to-navigate works
 * - Scheduled for tomorrow so the day-before-confirm card surfaces
 */
export async function createTestJob(companyId, ownerId) {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const ymd = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, "0")}-${String(tomorrow.getDate()).padStart(2, "0")}`;
  const stamp = Date.now();
  const stops = [
    {
      id: `test_pickup_${stamp}`,
      type: "pickup",
      locationName: "Test Pickup — Reeds Jewelers",
      address: "4400 Sharon Rd, Charlotte, NC 28211, USA",
      lat: 35.1538, lng: -80.8235,
      scheduledTime: "10:00",
    },
    {
      id: `test_pickup2_${stamp}`,
      type: "pickup",
      locationName: "Test Pickup — Diamonds Direct",
      address: "4521 Sharon Rd, Charlotte, NC 28211, USA",
      lat: 35.1531, lng: -80.8225,
      scheduledTime: "10:30",
    },
    {
      id: `test_airport_${stamp}`,
      type: "airport",
      locationName: "CLT",
      address: "5500 Josh Birmingham Pkwy, Charlotte, NC 28208, USA",
      lat: 35.2140, lng: -80.9431,
      scheduledTime: "",
    },
  ];
  return addDoc(collection(db, "jobs"), {
    jobNumber: `TEST-${stamp.toString().slice(-6)}`,
    jobType: "outbound",
    brokerName: "Test Broker (sandbox)",
    dispatcherName: "Sandbox",
    salesRepName: "",
    salesRepPhone: "",
    airportCode: "CLT",
    scheduledDate: ymd,
    scheduledTime: "10:00",
    numGuards: 1,
    guardAssignments: [],
    guardIds: [],
    stops,
    packages: [],
    status: "pool",
    invoiceStatus: "pending",
    notes: "🧪 Sandbox test job — created for guard onboarding. Safe to delete.",
    isTestJob: true,
    companyId,
    createdBy: ownerId,
    createdAt: serverTimestamp(),
  });
}

export async function deleteJobHard(jobId, companyId) {
  if (!jobId) throw new Error("jobId required");
  // Wipe draft invoices first so they don't dangle pointing at a missing job.
  // The query MUST be scoped by companyId: the invoices read rule requires
  // inMyCompany(resource), so Firestore rejects an unscoped query outright
  // (permission-denied) — which would silently leave the drafts orphaned.
  if (!companyId) {
    console.warn("deleteJobHard called without companyId — skipping invoice cleanup; drafts may be orphaned.");
  } else {
    try {
      const invSnap = await getDocs(query(
        collection(db, "invoices"),
        where("companyId", "==", companyId),
        where("jobId", "==", jobId),
      ));
      await Promise.all(
        invSnap.docs
          .filter((d) => d.data().status === "draft")
          .map((d) => deleteDoc(d.ref))
      );
    } catch (err) {
      // Non-fatal — keep going and delete the job itself.
      console.warn("Failed to clean up invoices during job delete:", err);
    }
  }
  return deleteDoc(doc(db, "jobs", jobId));
}

// ── LOCATIONS ──────────────────────────────────────────────
export async function getLocations(companyId) {
  const q = query(
    collection(db, "locations"),
    where("companyId", "==", companyId),
    orderBy("name")
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// Live listener version — preferred for the Locations page so auto-saved
// stops appear immediately without a refresh.
export function watchLocations(companyId, callback) {
  const q = query(
    collection(db, "locations"),
    where("companyId", "==", companyId),
    orderBy("name")
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
}

// One-time backfill helper — sweeps all of this company's jobs and upserts
// every non-airport stop with a valid name+address into the Locations
// collection. Idempotent (upsertLocationFromStop dedupes by name+address
// but DOES increment jobCount, so don't run this more than once).
export async function backfillLocationsFromJobs(companyId) {
  if (!companyId) return { scanned: 0, created: 0 };
  const snap = await getDocs(query(
    collection(db, "jobs"),
    where("companyId", "==", companyId),
  ));
  let scanned = 0, created = 0;
  // Track counts per match-key so repeated stops in the same sweep
  // merge into one Location with the correct jobCount rather than
  // creating duplicates across concurrent upserts.
  for (const jobDoc of snap.docs) {
    const stops = jobDoc.data().stops || [];
    for (const s of stops) {
      if (s.type === "airport") continue;
      if (!s.locationName || !s.address) continue;
      scanned++;
      try {
        const id = await upsertLocationFromStop(companyId, s);
        if (id) created++;
      } catch (e) { /* swallow */ }
    }
  }
  return { scanned, created };
}

export async function createLocation(data) {
  return addDoc(collection(db, "locations"), {
    ...data,
    jobCount: 0,
    createdAt: serverTimestamp(),
  });
}

export async function updateLocation(id, data) {
  return updateDoc(doc(db, "locations", id), data);
}

// Hard-delete a location entry. Existing jobs that referenced this
// location aren't affected — the locations collection is a reference
// directory, not a foreign key. Deleting just removes the entry from
// future autocomplete + the Locations list. Use this to clean up
// incorrect/duplicate entries (free-typed addresses that should have
// been a Places-verified existing one).
export async function deleteLocation(id) {
  return deleteDoc(doc(db, "locations", id));
}

// Normalize a string for match-key comparison: lowercase, trim, collapse whitespace,
// strip trailing punctuation. Used to dedupe auto-saved stops.
function normKey(s) {
  return (s || "").toString().toLowerCase().replace(/\s+/g, " ").replace(/[.,;]+$/g, "").trim();
}

// Best-effort extraction of a 10-digit US phone number out of free-form
// text (job notes, special-instructions strings, etc.). Returns a string
// formatted "(540) 875-8468" if found, else null. Accepts a variety of
// input shapes:
//   5408758468            → match
//   540.875.8468          → match
//   540-875-8468          → match
//   (540) 875-8468        → match
//   call 540 875 8468 pls → match
// We pull the FIRST plausible 10-digit run; multi-phone notes are rare
// and we don't want to guess which one is the recipient's.
function extractPhoneFromText(text) {
  if (!text || typeof text !== "string") return null;
  // Strip everything except digits, then find a 10-digit window. Also
  // allow a leading "1" country code (so "15408758468" still hits).
  const onlyDigits = text.replace(/[^\d]/g, "");
  const m10 = onlyDigits.match(/(?:^|[^\d])(1?)(\d{10})(?:$|[^\d])/);
  // Above regex won't match the "1?" prefix when run against the
  // digits-only string (no boundaries). Simpler: look for any 10
  // consecutive digits; if the run is 11 long and starts with 1, drop
  // the leading 1.
  const run = onlyDigits.match(/\d{10,11}/);
  if (!run) return null;
  let digits = run[0];
  if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
  if (digits.length !== 10) return null;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

// Upsert a location from a job stop. Matches on normalized name + address.
// Skips airport stops and stops missing a real address. Creates on first sight,
// increments jobCount on subsequent matches. Safe to call on every job save.
//
// options:
//   notes — free-form text from the job's notes/special-instructions
//           field. If a phone number is parseable out of it AND we're
//           creating a NEW location (not bumping an existing one), the
//           phone is stamped onto the new location's contacts[] as a
//           starter contact named after the location (e.g. "JEFFREY
//           HUBBARD"). We never touch contacts on an existing match —
//           the owner may have already curated them and we don't want
//           to overwrite or duplicate.
// mergeParsedContact moved to contactMerge.js (pure + unit-tested).
// Rules there: fill blanks on a same-person row, append genuinely new
// contacts, and keep a same-name-but-clearly-different-number as a
// second row so both numbers survive a POD re-parse.

export async function upsertLocationFromStop(companyId, stop, options = {}) {
  if (!companyId || !stop) return null;
  if (stop.type === "airport") return null;
  const name    = (stop.locationName || "").trim();
  const address = (stop.address || "").trim();
  if (!name || !address) return null;

  // Resolve coordinates: prefer the stop's existing lat/lng (set when
  // the address was picked from Google Places autocomplete). When the
  // address was free-typed or pasted from an email parse, try to
  // geocode it now so we can still promote it into the directory —
  // free-typed addresses used to be silently dropped here, which left
  // the owner staring at a Locations directory that didn't grow even
  // after creating jobs.
  let lat = typeof stop.lat === "number" ? stop.lat : null;
  let lng = typeof stop.lng === "number" ? stop.lng : null;
  if (lat == null || lng == null) {
    try {
      // Dynamic import to keep the firebase-only paths free of the
      // Maps JS API loader (and avoid circular import worries).
      const mod = await import("./mapsDistance");
      const c = await mod.geocode(address);
      if (c && typeof c.lat === "number" && typeof c.lng === "number") {
        lat = c.lat;
        lng = c.lng;
      }
    } catch (err) {
      console.warn("upsertLocationFromStop: geocode failed", err);
    }
  }
  // Still no coords after the geocode fallback — give up; we don't
  // want unverifiable addresses polluting the directory.
  if (lat == null || lng == null) return null;

  // Fast path: caller knows exactly which saved Location this stop
  // belongs to (e.g. the JobCreate parser matched it and the owner
  // may have edited the name in the parsed-detail row). Update that
  // doc directly — sync any rename + address change, bump jobCount,
  // skip the dedup search. Without this, an edited name would no
  // longer match the dedup key and we'd create a duplicate row.
  if (stop.locationId) {
    try {
      const ref = doc(db, "locations", stop.locationId);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const prev = snap.data().jobCount || 0;
        const patch = {
          name,
          address,
          lat,
          lng,
          jobCount: prev + 1,
          lastUsedAt: serverTimestamp(),
        };
        // Backfill the Google Place ID if this stop carries one and the
        // saved doc predates Place-ID capture.
        if (stop.placeId && !snap.data().placeId) patch.placeId = stop.placeId;
        // Append parser-supplied contact if it isn't already on the
        // location. Existing contacts are otherwise left untouched —
        // we only ADD, never overwrite, since the owner may have
        // edited contact rows by hand.
        const mergedContacts = mergeParsedContact(snap.data().contacts, stop);
        if (mergedContacts) patch.contacts = mergedContacts;
        await updateDoc(ref, patch);
        return stop.locationId;
      }
      // locationId pointed at a now-deleted doc; fall through to the
      // dedup-by-name path and create fresh.
    } catch (err) {
      console.warn("upsertLocationFromStop: by-id update failed, falling back", err);
    }
  }

  // Load this company's locations once and match in memory (small collection),
  // using the shared identity matcher so this background save agrees with the
  // job form's preview.
  const snap = await getDocs(query(
    collection(db, "locations"),
    where("companyId", "==", companyId),
  ));
  const locs = snap.docs.map((d) => ({ id: d.id, ref: d.ref, ...d.data() }));

  // Only AUTO-link on an EXACT identity match (Google Place ID, or identical
  // name + address). Weaker geo/address/name matches are deliberately NOT
  // merged here — the job form surfaces those for the owner to confirm
  // (link vs. create) before saving. That keeps this background path from
  // ever silently folding two different storefronts into one.
  const candidate = { name, address, lat, lng, placeId: stop.placeId || null };
  const { match, confidence } = findLocationMatch(locs, candidate);
  const existing = confidence === "exact" ? match : null;

  if (existing) {
    const prev = existing.jobCount || 0;
    const patch = { jobCount: prev + 1, lastUsedAt: serverTimestamp() };
    // Backfill the Google Place ID once we learn it for a doc created
    // before Place IDs were captured.
    if (candidate.placeId && !existing.placeId) patch.placeId = candidate.placeId;
    // Same parser-supplied contact merge as the by-id fast path —
    // dedup-matched locations should also pick up a new contact when
    // the parser found one.
    const mergedContacts = mergeParsedContact(existing.contacts, stop);
    if (mergedContacts) patch.contacts = mergedContacts;
    await updateDoc(existing.ref, patch);
    return existing.id;
  }

  // First time seeing this (name, address) pair — create the directory
  // entry. Seed contacts[] from (in priority order):
  //   1. Explicit stop.contactName / contactPhone / contactEmail — set
  //      by the email/POD parser when the source clearly named a person
  //      (e.g. a Ferrari paper form's Pickup Information block).
  //   2. A phone number scraped out of the notes string the caller
  //      passed in — uses the location's own name as a fallback contact
  //      label, which is the best we can do without a person's name.
  // (Existing locations: we never touch contacts on update.)
  const contactName  = (stop.contactName  || "").trim();
  const contactPhone = (stop.contactPhone || "").trim();
  const contactEmail = (stop.contactEmail || "").trim();
  let contacts = [];
  if (contactName || contactPhone || contactEmail) {
    contacts = [{
      name:  contactName  || name, // fall back to location name if person not named
      phone: contactPhone || "",
      email: contactEmail || "",
    }];
  } else {
    const phone = extractPhoneFromText(options.notes);
    if (phone) contacts = [{ name, phone, email: "" }];
  }

  const newDoc = await addDoc(collection(db, "locations"), {
    companyId,
    name,
    address,
    customerName: "",
    type: "",
    lat,
    lng,
    // Canonical Google Place ID when the address came from Places — the
    // strongest future dedup key. Null for free-typed/parsed addresses.
    placeId: stop.placeId || null,
    contacts,
    jobCount:   1,
    createdAt:  serverTimestamp(),
    lastUsedAt: serverTimestamp(),
  });
  return newDoc.id;
}

// ── LOCATION MERGE (duplicate cleanup) ─────────────────────────
// Merge one or more duplicate locations into a single surviving doc:
//   1. Repoint every job stop that referenced a duplicate to the survivor.
//   2. Fold contacts (deduped), jobCount (summed), and brokerStats recall
//      history (the Ferrari "usually bills" samples) into the survivor;
//      fill any blank survivor fields (placeId, coords, address, …) from a
//      duplicate so no data is lost.
//   3. Delete the duplicate docs.
// Returns { jobsRepointed }.
export async function mergeLocations(companyId, survivorId, duplicateIds) {
  if (!companyId || !survivorId || !Array.isArray(duplicateIds)) return { jobsRepointed: 0 };
  const dupSet = new Set(duplicateIds.filter((id) => id && id !== survivorId));
  if (dupSet.size === 0) return { jobsRepointed: 0 };

  const survSnap = await getDoc(doc(db, "locations", survivorId));
  if (!survSnap.exists()) throw new Error("Survivor location not found");
  const survivor = { id: survSnap.id, ...survSnap.data() };
  const dups = [];
  for (const id of dupSet) {
    // eslint-disable-next-line no-await-in-loop
    const s = await getDoc(doc(db, "locations", id));
    if (s.exists()) dups.push({ id: s.id, ...s.data() });
  }
  if (dups.length === 0) return { jobsRepointed: 0 };

  // 1. Repoint job stops referencing any duplicate → survivor.
  let jobsRepointed = 0;
  const jobsSnap = await getDocs(query(
    collection(db, "jobs"),
    where("companyId", "==", companyId),
  ));
  for (const jd of jobsSnap.docs) {
    const stops = Array.isArray(jd.data().stops) ? jd.data().stops : [];
    let changed = false;
    const newStops = stops.map((s) => {
      if (s && s.locationId && dupSet.has(s.locationId)) {
        changed = true;
        return { ...s, locationId: survivorId };
      }
      return s;
    });
    if (changed) {
      // eslint-disable-next-line no-await-in-loop
      await updateDoc(jd.ref, { stops: newStops });
      jobsRepointed += 1;
    }
  }

  // 2. Fold data into the survivor.
  const patch = {
    jobCount: (Number(survivor.jobCount) || 0)
      + dups.reduce((sum, d) => sum + (Number(d.jobCount) || 0), 0),
  };
  // Fill blank survivor scalars from the first duplicate that has them.
  for (const f of ["placeId", "lat", "lng", "address", "customerName", "type", "accessNotes"]) {
    const cur = survivor[f];
    if (cur == null || cur === "") {
      const donor = dups.find((d) => d[f] != null && d[f] !== "");
      if (donor) patch[f] = donor[f];
    }
  }
  patch.contacts = mergeContactLists([survivor, ...dups]);
  patch.brokerStats = mergeBrokerStats([survivor, ...dups]);
  await updateDoc(doc(db, "locations", survivorId), patch);

  // 3. Delete the duplicates.
  for (const d of dups) {
    // eslint-disable-next-line no-await-in-loop
    await deleteDoc(doc(db, "locations", d.id));
  }
  return { jobsRepointed };
}

// Union of contact lists across locations, deduped by normalized
// name + digits-only phone.
function mergeContactLists(locs) {
  const out = [];
  const seen = new Set();
  for (const l of locs) {
    const contacts = Array.isArray(l.contacts) ? l.contacts : [];
    for (const c of contacts) {
      const key = `${normKey(c.name)}|${(c.phone || "").replace(/\D/g, "")}`;
      if (key === "|") continue; // empty contact
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(c);
    }
  }
  return out;
}

// Merge brokerStats recall history across locations, per broker key.
// Samples are deduped by jobId; last* fields recomputed from the most
// recent sample so the Ferrari "usually bills" average survives a merge.
function mergeBrokerStats(locs) {
  const out = {};
  const keys = new Set();
  locs.forEach((l) => Object.keys(l.brokerStats || {}).forEach((k) => keys.add(k)));
  for (const key of keys) {
    const byJob = new Map();
    const loose = [];
    for (const l of locs) {
      const entry = l.brokerStats?.[key];
      if (!entry) continue;
      let samples = Array.isArray(entry.samples) ? entry.samples : [];
      if (samples.length === 0 && (entry.lastHours != null || entry.lastMiles != null)) {
        samples = [{
          jobId: entry.lastJobId || null,
          hours: Number(entry.lastHours) || 0,
          miles: Number(entry.lastMiles) || 0,
          billedAt: entry.lastBilledAt || null,
        }];
      }
      for (const s of samples) {
        if (s.jobId) byJob.set(s.jobId, s);
        else loose.push(s);
      }
    }
    let merged = [...byJob.values(), ...loose];
    merged.sort((a, b) => String(a.billedAt || "").localeCompare(String(b.billedAt || "")));
    if (merged.length > 50) merged = merged.slice(merged.length - 50);
    const last = merged[merged.length - 1] || null;
    out[key] = {
      samples: merged,
      lastHours: last ? last.hours : null,
      lastMiles: last ? last.miles : null,
      lastBilledAt: last ? last.billedAt : null,
      lastJobId: last ? last.jobId : null,
    };
  }
  return out;
}

// ── ACCOUNT REPS ───────────────────────────────────────────
export async function getAccountReps(companyId) {
  const q = query(
    collection(db, "accountReps"),
    where("companyId", "==", companyId),
    orderBy("name")
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function createAccountRep(data) {
  return addDoc(collection(db, "accountReps"), {
    ...data,
    createdAt: serverTimestamp(),
  });
}

// ── PHOTOS ─────────────────────────────────────────────────
export async function logPhoto(data) {
  return addDoc(collection(db, "photos"), {
    ...data,
    takenAt: serverTimestamp(),
  });
}

export async function getJobPhotos(jobId) {
  const q = query(
    collection(db, "photos"),
    where("jobId", "==", jobId),
    orderBy("takenAt")
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export function watchJobPhotos(jobId, companyId, callback) {
  // Filter by companyId too — the read rule requires companyId == my company,
  // and Firestore rejects a query it can't prove is safe (a jobId-only query
  // can't), which would make the listen fail silently and the feed look empty.
  const q = query(
    collection(db, "photos"),
    where("jobId", "==", jobId),
    where("companyId", "==", companyId),
    orderBy("takenAt")
  );
  return onSnapshot(
    q,
    (snap) => callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => { console.warn("watchJobPhotos failed", err); callback([]); }
  );
}

// ── FINANCIAL SUMMARIES ────────────────────────────────────
export async function getMonthlySummary(companyId, month) {
  const id = `${companyId}_${month}`;
  const snap = await getDoc(doc(db, "monthlySummaries", id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export function watchMonthlySummary(companyId, month, callback) {
  const id = `${companyId}_${month}`;
  return onSnapshot(doc(db, "monthlySummaries", id), (snap) => {
    callback(snap.exists() ? { id: snap.id, ...snap.data() } : null);
  });
}

export async function updateMonthlySummary(companyId, month, data) {
  const id = `${companyId}_${month}`;
  const ref = doc(db, "monthlySummaries", id);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    return updateDoc(ref, data);
  } else {
    return setDoc(ref, { companyId, month, ...data });
  }
}

export async function getGuardEarnings(guardId, month) {
  const id = `${guardId}_${month}`;
  const snap = await getDoc(doc(db, "guardEarnings", id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

// Watch a guard's earnings — derived from their per-invoice records in
// the `invoices` collection. Originally this read from a separate
// `guardEarnings` collection that was never written; now we just shape
// invoice docs into the same { month, amount, hours, jobNumber,
// brokerName, status, jobCount } structure the GuardEarnings UI
// expects. Status reflects whether the OWNER has marked the guard paid
// (guardPaidAmount), independent of the broker invoice's own status.
export function watchGuardEarnings(guardId, callback) {
  const q = query(
    collection(db, "invoices"),
    where("guardId", "==", guardId),
  );
  return onSnapshot(q, (snap) => {
    const earnings = snap.docs
      // Include an invoice on the guard's earnings screen when either:
      //   (a) the courier owner has actually recorded a payment
      //       (guardPaidAmount != null) — money in the guard's pocket
      //       is always shown, even for a cancelled trip the owner chose
      //       to pay for anyway; OR
      //   (b) the trip really happened (payGuard !== false) — surfaces
      //       as a "pending" row so the guard knows what jobs are still
      //       awaiting payment.
      // Cancelled-and-unpaid invoices are hidden entirely.
      .filter((d) => {
        const data = d.data();
        if (data.guardPaidAmount != null) return true;
        return data.payGuard !== false;
      })
      .map((d) => {
      const inv = d.data();
      const completedAt = inv.completedAt;
      // Resolve to a month string YYYY-MM. completedAt may be a Firestore
      // Timestamp, an ISO string, or a millis number — handle all three.
      let monthStr = "";
      try {
        const ms = typeof completedAt === "number" ? completedAt
          : completedAt?.toMillis?.() ?? Date.parse(completedAt);
        if (Number.isFinite(ms)) {
          const dt = new Date(ms);
          monthStr = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
        }
      } catch { /* leave blank */ }
      // Most older invoices saved a placeholder instead of a completion date
      // (see invoices.js), so they had no month and vanished from Earnings.
      // Fall back to the job's scheduled date.
      if (!monthStr && /^\d{4}-\d{2}/.test(inv.scheduledDate || "")) monthStr = inv.scheduledDate.slice(0, 7);
      // The guard-side amount is ALWAYS what the courier owner has
      // recorded as paid. We never expose the broker-bill amount to
      // guards — that number is the courier's private margin. Rows
      // with no payment yet get amount=0 and status="pending"; the UI
      // renders "$—" for those instead of a dollar figure.
      const isPaid = inv.guardPaidAmount != null;
      const actual = Number(inv.guardPaidAmount) || 0;
      return {
        id: d.id,
        month: monthStr,
        amount: isPaid ? actual : 0,
        hours: Number(inv.hoursCharged) || 0,
        jobCount: 1,
        jobNumber: invoiceLabel(inv),
        brokerName: inv.brokerName || "",
        // "paid" iff the owner has stamped guardPaidAmount on the invoice.
        // The broker's own invoice status (sent/paid/void) is separate.
        status: isPaid ? "paid" : "pending",
        guardPaidAmount: inv.guardPaidAmount ?? null,
        guardPaidAt: inv.guardPaidAt ?? null,
        payCycle: inv.payCycle || null,
      };
    });
    // Newest month first so the GuardEarnings list and YTD table don't
    // need to re-sort downstream.
    earnings.sort((a, b) => (b.month || "").localeCompare(a.month || ""));
    callback(earnings);
  });
}

// ── SINGLE JOB WATCHER ────────────────────────────────────
export function watchJob(jobId, callback) {
  return onSnapshot(doc(db, "jobs", jobId), (snap) => {
    callback(snap.exists() ? { id: snap.id, ...snap.data() } : null);
  });
}

export async function updateJobStops(jobId, stops) {
  return updateDoc(doc(db, "jobs", jobId), { stops });
}

// ── GUARD ACTIVE JOBS ──────────────────────────────────────
export function watchMyActiveJobs(guardId, callback, onError) {
  // Note: Firestore does not allow combining array-contains with 'in' in one query.
  // Fetch all jobs for this guard and filter by status client-side.
  const q = query(
    collection(db, "jobs"),
    where("guardIds", "array-contains", guardId),
    orderBy("createdAt", "desc")
  );
  const active = new Set(["grabbed", "confirmed", "inProgress"]);
  return onSnapshot(
    q,
    (snap) => {
      callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((j) => active.has(j.status)));
    },
    (err) => {
      // Without this handler a failed listener silently never fires, leaving
      // the guard stuck on "Loading…" forever. Surface it instead.
      console.error("watchMyActiveJobs listener error", err);
      if (onError) onError(err);
    }
  );
}

// ── AUDIT LOG ──────────────────────────────────────────────
export async function logAudit(data) {
  return addDoc(collection(db, "auditLog"), {
    ...data,
    timestamp: serverTimestamp(),
  });
}

/**
 * Aggregate guard-release events across the whole company so the owner
 * can spot patterns ("who keeps grabbing then bailing?"). Reads from the
 * audit log, not from individual jobs, so we get one query instead of N.
 *
 * Returns an array sorted by release count desc:
 *   [{ guardId, guardName, releaseCount, avgHeldMinutes, lastReleasedAt }]
 *
 * Optional `sinceDays` clips to recent activity (default 90 days). Pass
 * 0 for all-time.
 */
export async function getGuardReleaseStats(companyId, sinceDays = 90) {
  const q = query(
    collection(db, "auditLog"),
    where("companyId", "==", companyId),
    where("action", "==", "guard_released_job"),
  );
  const snap = await getDocs(q);
  const cutoff = sinceDays > 0 ? Date.now() - sinceDays * 86400000 : 0;

  const byGuard = new Map();
  for (const d of snap.docs) {
    const r = d.data();
    const ts = r.timestamp?.toDate?.() || (r.timestamp ? new Date(r.timestamp) : null);
    if (cutoff && ts && ts.getTime() < cutoff) continue;
    if (!r.guardId) continue;
    const cur = byGuard.get(r.guardId) || {
      guardId: r.guardId,
      guardName: r.guardName || "",
      releaseCount: 0,
      heldMinutesSum: 0,
      heldMinutesSamples: 0,
      lastReleasedAt: null,
      // Breakdown by previous status — releases AFTER confirmation are
      // worse than releases while still in "grabbed" (rep hadn't been
      // notified yet). Helps the owner triage.
      releasedAfterConfirm: 0,
    };
    cur.releaseCount += 1;
    if (typeof r.heldMinutes === "number") {
      cur.heldMinutesSum += r.heldMinutes;
      cur.heldMinutesSamples += 1;
    }
    if (ts && (!cur.lastReleasedAt || ts > cur.lastReleasedAt)) {
      cur.lastReleasedAt = ts;
    }
    if (r.previousStatus === "confirmed" || r.previousStatus === "inProgress") {
      cur.releasedAfterConfirm += 1;
    }
    // Refresh name in case it was missing on an older entry.
    if (!cur.guardName && r.guardName) cur.guardName = r.guardName;
    byGuard.set(r.guardId, cur);
  }

  return [...byGuard.values()]
    .map((g) => ({
      guardId: g.guardId,
      guardName: g.guardName,
      releaseCount: g.releaseCount,
      releasedAfterConfirm: g.releasedAfterConfirm,
      avgHeldMinutes: g.heldMinutesSamples
        ? Math.round(g.heldMinutesSum / g.heldMinutesSamples)
        : null,
      lastReleasedAt: g.lastReleasedAt,
    }))
    .sort((a, b) => b.releaseCount - a.releaseCount);
}

export async function getJobAuditLog(jobId) {
  const q = query(
    collection(db, "auditLog"),
    where("jobId", "==", jobId),
    orderBy("timestamp")
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ── INVITES ────────────────────────────────────────────────
export async function createInvite(data) {
  return addDoc(collection(db, "invites"), {
    ...data,
    used: false,
    createdAt: serverTimestamp(),
    expiresAt: new Date(Date.now() + 48 * 60 * 60 * 1000),
  });
}

export async function getInvite(inviteId) {
  const snap = await getDoc(doc(db, "invites", inviteId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function markInviteUsed(inviteId) {
  return updateDoc(doc(db, "invites", inviteId), { used: true });
}
